/**
 * RoutingCategorySelect - Select de categoria para Ação Rápida
 *
 * Usado nos dialogs de criar/editar regra manual. Lista as categorias
 * visíveis ao usuário, opção "Sem categoria" e atalho "+ Nova categoria"
 * que abre o RoutingCategoryDialog.
 *
 * CHANGELOG:
 *   2026-06-23 v1.0.0 - Versão inicial.
 */

import React, { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Plus, FolderTree, Lock, Users, Globe } from 'lucide-react';
import { useRoutingRuleCategories } from '@/hooks/useRoutingRuleCategories';
import { RoutingCategoryDialog } from './RoutingCategoryDialog';
import { RoutingCategoryIcon } from './RoutingCategoryIcon';

interface RoutingCategorySelectProps {
  /** ID da categoria atual (null = sem categoria) */
  value: string | null;
  /** Callback ao mudar */
  onChange: (categoryId: string | null) => void;
  /** Mostra label acima do select */
  showLabel?: boolean;
  disabled?: boolean;
}

const NONE_VALUE = '__none__';

/**
 * Mapeia visibilidade em ícone pequeno para o select.
 */
function VisibilityIcon({ v }: { v: string }) {
  if (v === 'public') return <Globe className="h-3 w-3 text-muted-foreground" />;
  if (v === 'shared') return <Users className="h-3 w-3 text-muted-foreground" />;
  return <Lock className="h-3 w-3 text-muted-foreground" />;
}

export function RoutingCategorySelect({
  value,
  onChange,
  showLabel = true,
  disabled,
}: RoutingCategorySelectProps) {
  const { categories, isLoading, refetch } = useRoutingRuleCategories();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Categoria atualmente selecionada (para fallback de display)
  const current = value ? categories.find((c) => c.id === value) : null;

  return (
    <div className="space-y-2">
      {showLabel && <Label>Categoria</Label>}
      <div className="flex items-center gap-2">
        <Select
          value={value ?? NONE_VALUE}
          onValueChange={(v) => onChange(v === NONE_VALUE ? null : v)}
          disabled={disabled || isLoading}
        >
          <SelectTrigger className="flex-1">
            <SelectValue placeholder="Selecione uma categoria">
              {current ? (
                <div className="flex items-center gap-2">
                  <RoutingCategoryIcon
                    name={current.icon}
                    color={current.color}
                    size={14}
                  />
                  <span className="truncate">{current.name}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <FolderTree className="h-3.5 w-3.5" />
                  <span>Sem categoria</span>
                </div>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_VALUE}>
              <div className="flex items-center gap-2">
                <FolderTree className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground italic">Sem categoria</span>
              </div>
            </SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                <div className="flex items-center gap-2">
                  <RoutingCategoryIcon name={c.icon} color={c.color} size={14} />
                  <span className="truncate">{c.name}</span>
                  <VisibilityIcon v={c.visibility} />
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Atalho para criar nova categoria */}
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => setDialogOpen(true)}
          title="Nova categoria"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      <RoutingCategoryDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={() => refetch()}
      />
    </div>
  );
}
